import { useMemo } from 'react'

import { useTokenCatalog } from '@/data/marketApi/hooks/useTokenCatalog'
import {
  buildVisibleCatalogCurrencies,
  currencyKey,
} from '@/features/swap/swapCatalog'
import {
  buildReceivePickerScrollList,
  getFeaturedCatalogCurrencies,
} from '@/features/tokens/tokenCatalogRuntime'
import { useImportableTokenSearchList } from '@/features/tokens/useImportableTokenSearchList'
import { ChainId, Currency, CurrencyAmount, JSBI } from '@/sdk'
import { useUserAddedTokens } from '@/state/user/hooks'
import { useCurrencyBalances } from '@/state/wallet/hooks'

export type MobileTokenPickerMode = 'pay' | 'receive' | 'list'

export { currencyKey }

export function useMobileTokenPickerGroups(
  chainId: ChainId,
  search: string,
  account: string | null | undefined,
  mode: MobileTokenPickerMode,
) {
  const userAddedTokens = useUserAddedTokens()
  const userAddedForChain = useMemo(
    () => userAddedTokens.filter((token) => token.chainId === chainId),
    [chainId, userAddedTokens],
  )
  const { entries } = useTokenCatalog()

  const selectableTokens = useMemo(() => {
    const byKey = new Map<string, Currency>()
    for (const currency of buildVisibleCatalogCurrencies(chainId, entries, userAddedForChain)) {
      byKey.set(currencyKey(currency), currency)
    }
    return [...byKey.values()]
  }, [chainId, entries, userAddedForChain])

  const quickPickTokens = useMemo(
    () => getFeaturedCatalogCurrencies(chainId, entries),
    [chainId, entries],
  )

  const receiveScrollListTokens = useMemo(
    () => buildReceivePickerScrollList(selectableTokens, quickPickTokens),
    [quickPickTokens, selectableTokens],
  )

  const {
    tokens: searchResults,
    searchToken,
    isAddressSearch,
    isResolvingAddress,
    importedByAddress,
    addressNotFound,
  } = useImportableTokenSearchList(chainId, search, selectableTokens)

  const isSearching = search.trim().length > 0 || isAddressSearch
  const balanceSource = isSearching ? searchResults : selectableTokens
  const balances = useCurrencyBalances(account ?? undefined, balanceSource)

  const balanceByKey = useMemo(() => {
    const map = new Map<string, CurrencyAmount>()
    balanceSource.forEach((currency, index) => {
      const balance = balances[index]
      if (balance) map.set(currencyKey(currency), balance)
    })
    return map
  }, [balanceSource, balances])

  const payGroups = useMemo(() => {
    if (mode === 'list' || mode !== 'pay' || isSearching) {
      return { withBalance: [] as Array<{ currency: Currency; balance: CurrencyAmount }>, withoutBalance: searchResults }
    }

    const withBalance: Array<{ currency: Currency; balance: CurrencyAmount }> = []
    const withoutBalance: Currency[] = []

    for (const currency of selectableTokens) {
      const balance = balanceByKey.get(currencyKey(currency))
      if (balance?.greaterThan(JSBI.BigInt(0))) {
        withBalance.push({ currency, balance })
      } else {
        withoutBalance.push(currency)
      }
    }

    withBalance.sort((a, b) => {
      if (a.balance.greaterThan(b.balance)) return -1
      if (b.balance.greaterThan(a.balance)) return 1
      return (a.currency.symbol ?? '').localeCompare(b.currency.symbol ?? '')
    })

    withoutBalance.sort((a, b) => (a.symbol ?? '').localeCompare(b.symbol ?? ''))

    return { withBalance, withoutBalance }
  }, [balanceByKey, isSearching, mode, searchResults, selectableTokens])

  const receiveGroups = useMemo(() => {
    if (mode === 'list') {
      return { featured: [] as Currency[], all: isSearching ? searchResults : selectableTokens }
    }
    if (mode !== 'receive' || isSearching) {
      return { featured: [] as Currency[], all: searchResults }
    }

    return { featured: quickPickTokens, all: receiveScrollListTokens }
  }, [isSearching, mode, quickPickTokens, receiveScrollListTokens, searchResults, selectableTokens])

  return {
    tokens: isSearching ? searchResults : selectableTokens,
    searchToken,
    isAddressSearch,
    isResolvingAddress,
    importedByAddress,
    addressNotFound,
    isSearching,
    payGroups,
    receiveGroups,
    balanceByKey,
  }
}

export type MobileTokenPickerGroups = ReturnType<typeof useMobileTokenPickerGroups>
